import { Link } from "react-router-dom";
import { MapPin, Navigation, Phone, Clock, ExternalLink, Star, MessageSquarePlus, Car } from "lucide-react";
import { restaurantInfo } from "../mock";
import Reveal from "./Reveal";
import { useT } from "../context/LanguageContext";
import { HOME_STRINGS, NAV_STRINGS } from "../i18n/strings";
import { trackEvent } from "../utils/analytics";

const ADDRESS_QS = encodeURIComponent(restaurantInfo.location.full);
const EMBED_URL = `https://www.google.com/maps?q=${ADDRESS_QS}&z=17&output=embed`;
const GMAPS_DIRECTIONS = `https://www.google.com/maps/dir/?api=1&destination=${ADDRESS_QS}&destination_place_id=${restaurantInfo.google.cid}`;
const APPLE_MAPS = `https://maps.apple.com/?daddr=${ADDRESS_QS}`;

// Google "G" — 4-color logo
function GoogleG({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

/**
 * Unified "Visit us" section — replaces the previous three separate
 * blocks (GoogleReviewsWidget + HomeMap + final "Come hungry" CTA).
 *
 * Combines: interactive map · address & hours · Google Reviews rating ·
 * primary CTAs (order online, directions, call, leave a review).
 *
 * Layout:
 *   - Desktop: map on the left (7 cols), info panel on the right (5 cols)
 *   - Mobile: info panel first, map below (visitor reads content, then sees map)
 */
export default function VisitSection({ compact = false }) {
  const t = useT();
  const { ratingValue, reviewCount, profileUrl, writeReviewShortUrl } = restaurantInfo.google;
  const fullStars = Math.floor(ratingValue);
  const hasHalf = ratingValue % 1 >= 0.4 && ratingValue % 1 < 0.9;

  const trackDir = (provider) => trackEvent("directions_click", { source: "visit_section", provider });
  const trackOrder = () => trackEvent("order_online_click", { source: "visit_section" });
  const trackReviews = (kind) => trackEvent("google_reviews_click", { source: "visit_section", kind });
  const trackPhone = () => trackEvent("phone_click", { source: "visit_section" });

  return (
    <section
      id="visit-us"
      className={`${compact ? "py-16 md:py-20" : "py-24 md:py-28"} bg-cream-50 relative overflow-hidden`}
      data-testid="visit-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <div className="relative rounded-[32px] overflow-hidden bg-cream border border-cream-100 shadow-lg">
            <div className="grid lg:grid-cols-12">
              {/* MAP */}
              <div className="lg:col-span-7 order-2 lg:order-1 relative min-h-[320px] lg:min-h-[560px] bg-cream-100">
                <iframe
                  title="Map showing Nina's Taqueria — 49 E Main Street, Los Gatos, CA"
                  src={EMBED_URL}
                  className="absolute inset-0 w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                  data-testid="visit-map-iframe"
                  style={{ border: 0 }}
                />
              </div>

              {/* INFO PANEL */}
              <div className="lg:col-span-5 order-1 lg:order-2 p-8 md:p-10 lg:p-12 flex flex-col">
                <div className="font-hand text-2xl text-brand mb-2 leading-none">
                  {t(HOME_STRINGS.finalHand.en, HOME_STRINGS.finalHand.es)}
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-semibold text-granite-dark leading-[1.05]">
                  <span className="block">{t(HOME_STRINGS.finalTitleLine1.en, HOME_STRINGS.finalTitleLine1.es)}</span>
                  <span className="block">{t(HOME_STRINGS.finalTitleLine2.en, HOME_STRINGS.finalTitleLine2.es)}</span>
                </h2>
                <p className="mt-4 text-granite/70 text-[15px] leading-relaxed">
                  {t(HOME_STRINGS.finalSub.en, HOME_STRINGS.finalSub.es)}
                </p>

                {/* Google rating pill */}
                <a
                  href={profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackReviews("rating_pill")}
                  data-testid="visit-google-rating"
                  className="mt-6 inline-flex items-center gap-3 self-start px-3 py-2 rounded-full bg-cream-50 border border-cream-100 hover:border-brand/40 transition-colors group"
                >
                  <GoogleG className="w-4 h-4" />
                  <span className="flex items-center gap-1.5">
                    <span className="font-display text-lg font-semibold text-granite-dark leading-none">
                      {ratingValue.toFixed(1)}
                    </span>
                    <span className="flex items-center gap-0.5" aria-label={`${ratingValue} out of 5`}>
                      {Array.from({ length: 5 }).map((_, i) => {
                        const filled = i < fullStars;
                        const half = i === fullStars && hasHalf;
                        return (
                          <span key={i} className="relative inline-block">
                            <Star className={`w-3.5 h-3.5 ${filled ? "fill-[#FBBC05] text-[#FBBC05]" : "text-cream-100"}`} strokeWidth={filled ? 0 : 1} />
                            {half && (
                              <span className="absolute inset-y-0 left-0 w-1/2 overflow-hidden">
                                <Star className="w-3.5 h-3.5 fill-[#FBBC05] text-[#FBBC05]" strokeWidth={0} />
                              </span>
                            )}
                          </span>
                        );
                      })}
                    </span>
                  </span>
                  <span className="text-xs text-granite/60 border-l border-cream-100 pl-3">
                    {reviewCount}+ {t("reviews", "reseñas")}
                  </span>
                </a>

                {/* Address / hours / phone */}
                <ul className="mt-6 space-y-2.5 text-[14px] text-granite/85">
                  <li className="flex items-start gap-2.5">
                    <MapPin className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                    <a
                      href={GMAPS_DIRECTIONS}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackDir("google_inline")}
                      className="hover:text-brand transition-colors"
                    >
                      {restaurantInfo.location.full}
                    </a>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Clock className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                    <span>{t("Open daily 10 AM – 8 PM", "Abierto todos los días 10:00 – 20:00")}</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Phone className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                    <a href={`tel:${restaurantInfo.phone}`} onClick={trackPhone} className="hover:text-brand transition-colors">
                      {restaurantInfo.phone}
                    </a>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Car className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                    <span className="text-granite/65">{t("Free public parking one street over.", "Estacionamiento público gratis a una calle.")}</span>
                  </li>
                </ul>

                {/* Primary CTAs */}
                <div className="mt-6 grid grid-cols-2 gap-2.5">
                  <a
                    href={restaurantInfo.externalLinks.orderOnline}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackOrder}
                    data-testid="visit-order-online"
                    className="inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-dark text-cream px-5 py-3 rounded-full font-semibold text-sm transition-all shadow-md hover:-translate-y-0.5"
                  >
                    {t(NAV_STRINGS.orderOnline.en, NAV_STRINGS.orderOnline.es)} <ExternalLink className="w-4 h-4" />
                  </a>
                  <a
                    href={GMAPS_DIRECTIONS}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackDir("google")}
                    data-testid="visit-directions"
                    className="inline-flex items-center justify-center gap-2 bg-granite-dark hover:bg-granite text-cream px-5 py-3 rounded-full font-semibold text-sm transition-all"
                  >
                    <Navigation className="w-4 h-4" /> {t("Directions", "Cómo llegar")}
                  </a>
                </div>

                {/* Secondary links */}
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px]">
                  <a
                    href={writeReviewShortUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackReviews("write_review")}
                    data-testid="visit-leave-review"
                    className="inline-flex items-center gap-1.5 text-granite-dark hover:text-brand transition-colors font-medium"
                  >
                    <MessageSquarePlus className="w-3.5 h-3.5" />
                    {t("Leave a review", "Deja una reseña")}
                  </a>
                  <a
                    href={APPLE_MAPS}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackDir("apple")}
                    data-testid="visit-apple-maps"
                    className="inline-flex items-center gap-1.5 text-granite/70 hover:text-brand transition-colors"
                  >
                    <Navigation className="w-3.5 h-3.5" /> {t("Apple Maps", "Apple Maps")}
                  </a>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-1.5 text-granite/70 hover:text-brand transition-colors ml-auto"
                  >
                    {t("Contact us", "Contáctanos")} →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
